import {
  Component,
  computed,
  DOCUMENT,
  effect,
  ElementRef,
  inject,
  InjectionToken,
  linkedSignal,
  signal,
} from '@angular/core';
import { sumBy } from 'lodash';
import { CssVarHelper } from '../../../helpers/css-var.helper';
import type {
  OnyxToast,
  OnyxToasterConfig,
  OnyxToastLayout,
} from '../interfaces';
import { OnyxToastComponent } from './onyx-toast/onyx-toast.component';

export const ONYX_TOASTER_CONFIG = new InjectionToken<OnyxToasterConfig>(
  'Toaster config',
);

@Component({
  selector: 'onyx-toaster',
  imports: [OnyxToastComponent],
  templateUrl: './onyx-toaster.component.html',
  styleUrl: './onyx-toaster.component.scss',
  host: {
    '(mouseenter)': `isHovered.set(true)`,
    '(mouseleave)': `isHovered.set(false)`,
    '(focusin)': `isFocused.set(true)`,
    '(focusout)': `updateIsFocused($event)`,
  },
})
export class OnyxToasterComponent {
  protected readonly config = inject(ONYX_TOASTER_CONFIG);
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  private readonly GAP = Number.parseFloat(
    CssVarHelper.read('--onyx-space-3-5', inject(DOCUMENT)),
  );

  protected isHovered = signal(false);
  protected isFocused = signal(false);
  protected isPaused = computed(() => this.isHovered() || this.isFocused());

  private heights = linkedSignal<OnyxToast[], Map<OnyxToast, number>>({
    source: this.config.toasts,
    computation: (toasts, previous) =>
      new Map(
        toasts
          .filter((toast) => previous?.value.has(toast))
          .map((toast) => [toast, previous!.value.get(toast)!]),
      ),
  });

  protected stack = computed(() => {
    const toasts = this.config.toasts();
    const heights = this.heights();
    const expanded = this.isPaused() && toasts.length > 1;
    const frontHeight = toasts.length ? heights.get(toasts[0]) : undefined;

    return toasts.map((toast, index) => {
      const offset = sumBy(
        toasts.slice(0, index),
        (toastInFront) => (heights.get(toastInFront) ?? 0) + this.GAP,
      );
      const layout: OnyxToastLayout = { index, expanded, offset, frontHeight };

      return { toast, layout };
    });
  });

  constructor() {
    // mouseleave never fires when the toast under the pointer is removed, so
    // reset once the toaster is empty — otherwise the next toast starts paused.
    effect(() => {
      if (this.config.toasts().length) return;

      this.isHovered.set(false);
      this.isFocused.set(false);
    });
  }

  protected updateIsFocused(event: FocusEvent): void {
    const target = event.relatedTarget as Node | null;
    this.isFocused.set(
      !!target && this.elementRef.nativeElement.contains(target),
    );
  }

  protected setHeight(toast: OnyxToast, height: number): void {
    this.heights.update((heights) => new Map(heights).set(toast, height));
  }
}
